import { extendTheme } from '@chakra-ui/react';

/**
 * Canonical "WP QA Dashboard" design tokens.
 * Values come straight from design/canonical/README.md § Design Tokens.
 * Fonts are loaded via the <link> in public/index.html.
 */
const colors = {
  brand: {
    50: '#ecfdf5',
    100: '#d1fae5', // tint border
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981', // primary green
    600: '#059669', // accent text
    700: '#047857', // deep
    800: '#065f46',
    900: '#064e3b',
  },
  // README neutral ramp (slate). 200 == the card border #e6eaef.
  gray: {
    50: '#f8fafc',
    100: '#f1f5f9',
    150: '#eef1f5', // inner border
    200: '#e6eaef', // card border
    250: '#e2e8ef', // control border
    300: '#cbd5e1', // disabled text
    400: '#94a3b8', // muted text
    500: '#64748b', // secondary text
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a', // primary text
  },

  // Test-outcome semantics. Always pair the colour with a label.
  status: {
    pass: '#059669',
    passText: '#047857',
    passBg: '#ecfdf5',
    passBorder: '#d1fae5',
    fail: '#dc2626',
    failText: '#b91c1c',
    failBg: '#fee2e2',
    failBorder: '#fecaca',
    flaky: '#d97706',
    flakyText: '#b45309',
    flakyBg: '#fef3c7',
    skip: '#94a3b8',
    skipBg: '#f1f5f9',
  },

  // Amber / warning accent.
  amber: {
    500: '#d97706',
    600: '#b45309',
    700: '#92400e',
    tint: '#fef3c7',
    onTint: '#78350f',
  },

  // Role badge palette (RoleBadge reads role.<role>Fg/Bg/Border).
  role: {
    ownerFg: '#92400e',
    ownerBg: '#fef3c7',
    ownerBorder: '#fde68a',
    adminFg: '#1d4ed8',
    adminBg: '#dbeafe',
    adminBorder: '#bfdbfe',
    developerFg: '#475569',
    developerBg: '#f1f5f9',
    developerBorder: '#e2e8ef',
  },

  // Informational / blue accent.
  info: {
    500: '#3b82f6',
    600: '#1d4ed8',
    50: '#eff6ff',
    100: '#dbeafe',
    text: '#1e3a5f',
  },
};

// 6 pill · 8–10 control · 12 card · 999 chip
const radii = {
  none: '0',
  sm: '6px',
  md: '8px',
  lg: '10px',
  xl: '12px',
  '2xl': '14px',
  full: '9999px',
};

const shadows = {
  card: '0 1px 2px rgba(15,23,42,0.04)',
  chip: '0 1px 2px rgba(15,23,42,0.08)',
  drawer: '-10px 0 32px rgba(15,23,42,0.14)',
  toast: '0 8px 24px rgba(15,23,42,0.18)',
  sheet: '0 1px 3px rgba(20,24,32,0.1)',
  outline: '0 0 0 2px rgba(16,185,129,0.5)',
};

const fonts = {
  heading: "'Public Sans', system-ui, -apple-system, sans-serif",
  body: "'Public Sans', system-ui, -apple-system, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};
const fontWeights = { normal: 400, medium: 500, semibold: 600, bold: 700 };
const fontSizes = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2rem',
  '5xl': '2.5rem',
};

const components = {
  Button: {
    baseStyle: { borderRadius: 'md', boxShadow: 'none', fontWeight: 'semibold' },
    defaultProps: { colorScheme: 'brand', variant: 'solid' },
  },
  Input: {
    baseStyle: { field: { borderRadius: 'md', boxShadow: 'none' } },
    variants: {
      filled: {
        field: {
          bg: 'white',
          borderColor: 'gray.250',
          _hover: { bg: 'gray.50' },
          _focus: { bg: 'white', borderColor: 'brand.500' },
        },
      },
    },
    defaultProps: { focusBorderColor: 'brand.500' },
  },
  Select: {
    baseStyle: { field: { borderRadius: 'md', boxShadow: 'none' } },
    defaultProps: { focusBorderColor: 'brand.500' },
  },
  Table: {
    variants: {
      simple: {
        th: {
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: '10.5px',
          fontWeight: '600',
          color: 'gray.400',
          bg: 'transparent',
          borderColor: 'gray.150',
        },
        td: { borderColor: 'gray.150', fontSize: '13.5px' },
      },
    },
    defaultProps: { variant: 'simple', size: 'sm' },
  },
};

const breakpoints = {
  base: '0em',
  sm: '30em',
  md: '48em',
  lg: '62em',
  xl: '80em',
  '2xl': '96em',
};

const theme = extendTheme({
  config: { initialColorMode: 'light', useSystemColorMode: false },
  colors,
  radii,
  shadows,
  fonts,
  fontWeights,
  fontSizes,
  components,
  breakpoints,
  styles: {
    global: {
      body: {
        bg: 'gray.50',
        color: 'gray.900',
        overflowX: 'hidden',
        fontSize: '14px',
        WebkitFontSmoothing: 'antialiased',
      },
      '*:focus-visible': {
        outline: '2px solid',
        outlineColor: 'brand.500',
        outlineOffset: '2px',
      },
      '::selection': { background: 'brand.100' },
      '@keyframes pulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.25 },
      },
    },
  },
});

export default theme;
